import { Box, BoxProps, Loader, ModBox, Spacer, SpacerNew } from '@jfs/ui-new'
import { useIsFocused } from '@react-navigation/core'
import { useNavigation } from '@react-navigation/native'
import { useEffect, useState } from 'react'
import ActionsMenu from '../../comps/ActionMenu/ActionsMenu'
import EmptyView from '../../comps/EmptyView'
import { NewTable } from '../../comps/NewTable'
import { TimeOut, Toast, ToastPosition, ToastType } from '../../comps/Toast'
import { transactionScreenStrings } from '../../data/static/strings'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import {
  Colors,
  getCustomColumnWidth,
  getErrorMessage,
  getLoadingState,
  getWidth,
  useWindowDimensions,
} from '../../util/util'
import { Highlights } from './Highlights'

const Vault = () => {
  const viewPortWidth = useWindowDimensions()
  const {
    mid,
    selectedDateInfo,
    updateSelectedDate,
    fetchTokenizationHistory,
    getVaultHistory,
    getTokenTxnHistoryList,
    exportVaultReports,
    fetchVaultColumns,
    fetchTokenTxnColumns,
    fields,
    resetPagination,
    toastMessage,
    toastResetMessage,
    setDownloadReportType,
    fetchTokenTxnHistory,
    tokenTxnFields,
    exportTxnFiles,
    updateTxnSelectedDate,
    txnsSelectedDateInfo,
  } = useStores((store) => ({
    selectedDateInfo: store.tokenizationRequests.selectedDate,
    txnsSelectedDateInfo: store.tokenizationRequests.txnsSelectedDate,
    updateSelectedDate: store.tokenizationRequests.updateDates,
    updateTxnSelectedDate: store.tokenizationRequests.updateTxnDates,
    fetchTokenizationHistory: store.tokenizationRequests?.fetchTokenizationHistory,
    fetchTokenTxnHistory: store.tokenizationRequests?.fetchTokenTxnHistory,
    mid: store?.user?.selectedMid,
    getVaultHistory: store.tokenizationRequests.getVaultHistory,
    getTokenTxnHistoryList: store.tokenizationRequests.getTokenTxnHistory,
    exportVaultReports: store.tokenizationRequests.exportVaultReports,
    fetchVaultColumns: store.tokenizationRequests.fetchVaultColumns,
    fetchTokenTxnColumns: store.tokenizationRequests.fetchTokenTxnColumns,
    fields: store.tokenizationRequests.fieldSetting,
    tokenTxnFields: store.transactions.fieldSetting,
    resetPagination: store.tokenizationRequests.resetPagination,
    toastMessage: store?.reports?.getMessage(),
    toastResetMessage: store?.reports?.resetMessage,
    setDownloadReportType: store.settings.metaData.setDownloadReportType,
    exportTxnFiles: store.tokenizationRequests.exportFilesTxnV3,
  }))
  const [value, setValue] = useState(0)

  const [getTokenizationHistory, historyApiHandler] = useLazyApiData(fetchTokenizationHistory)
  const [getTokenTxnHistory, tokenTxnApiHandler] = useLazyApiData(fetchTokenTxnHistory)
  const [getVaultColumns, columnsApiHandler] = useLazyApiData(fetchVaultColumns)
  const [getTokenTxnsColumns, txnsColumnsApiHandler] = useLazyApiData(fetchTokenTxnColumns)
  const [exportVaultReportsApi, exportVaultReportsHandler] = useLazyApiData(exportVaultReports)
  const [exportFile, exportFileHandler] = useLazyApiData(exportTxnFiles)

  const [fieldSettingVisible, setFieldSettingVisible] = useState(false)
  const [filterVisible, setFilterVisible] = useState(false)
  const isFocused = useIsFocused()
  const navigate = useNavigation().navigate

  useEffect(() => {
    if (value === 0) {
      if (columnsApiHandler.state === 'fulfilled' && mid) {
        getTokenizationHistory()
      }
    } else {
      if (txnsColumnsApiHandler.state === 'fulfilled' && mid) {
        getTokenTxnHistory()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsApiHandler.state, getTokenizationHistory, mid, txnsColumnsApiHandler.state])

  const fetchData = () => {
    if (value === 0) {
      if (fields.length === 0) {
        getVaultColumns()
      } else {
        getTokenizationHistory()
      }
    } else {
      if (tokenTxnFields.length === 0) {
        getTokenTxnsColumns()
      } else {
        getTokenTxnHistory()
      }
    }
  }
  useEffect(() => {
    if (mid && isFocused) {
      resetPagination()
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, mid, value])

  const handleRetry = () => {
    if (mid && isFocused) {
      fetchData()
    }
  }

  const listData = value === 0 ? getVaultHistory() : getTokenTxnHistoryList()
  const apis = [historyApiHandler, columnsApiHandler, tokenTxnApiHandler]
  const loadingState = getLoadingState(apis)
  const errorState = getErrorMessage(apis)
  const emptyState = listData.length > 1 && !loadingState

  const getEmptyMessage = (): string => {
    return listData.length === 0 ? transactionScreenStrings.noRecords : transactionScreenStrings.empty
  }

  const handleSearch = (custId: string) => {
    resetPagination()
    getTokenizationHistory(custId)
  }
  const handleToastAction = () => {
    setDownloadReportType('VAULT')
    toastResetMessage()
    navigate(Routes.DOWNLOADS)
  }
  const handleToastDismiss = () => {
    toastResetMessage()
  }

  const scrollViewStyle = () => {
    if (listData.length === 0) {
      return styles.emptyContainer
    }
    return styles.tableContainer(loadingState)
  }

  const scrollStyle = scrollViewStyle()
  return (
    <Box flex={1} overflowY={'hidden'}>
      <Toast
        message={toastMessage}
        hide={toastMessage.message === ''}
        position={ToastPosition.TOP_POSITIVE_ACTION_VIEW}
        type={ToastType.POSITIVE_ACTION_VIEW}
        onDismiss={handleToastDismiss}
        onAction={handleToastAction}
        actionLabel={'Go To Downloads'}
        timeOut={TimeOut.FIVE_SECONDS}
      />
      <Highlights />
      <Spacer direction={'vertical'} size={16} />
      <Box {...styles.container}>
        <ActionsMenu
          path={Routes.VAULT_TOKENIZATION}
          handleExport={value === 0 ? exportVaultReportsApi : exportFile}
          fetchDetailOfPath={fetchData}
          fieldSettingVisible={fieldSettingVisible}
          filterVisible={filterVisible}
          selectedDateInfo={value === 0 ? selectedDateInfo : txnsSelectedDateInfo}
          updateSelectedDate={value === 0 ? updateSelectedDate : updateTxnSelectedDate}
          maxRange={31}
          setFieldSettingVisible={setFieldSettingVisible}
          setFilterVisible={setFilterVisible}
          handleTokenizationSearch={handleSearch}
          selectedTabIndex={value}
          setSelectedTabIndex={setValue}
          showCalendarOnRight={true}
          showFieldSettingsAndFilters={value === 1}
          hideFieldSettings={true}
        />
        <Spacer direction={'vertical'} size={16} />
        <EmptyView
          onRetry={handleRetry}
          isLoading={loadingState}
          error={errorState}
          emptyMessage={getEmptyMessage()}
          loadingMessage={'fetching records..'}
          hide={fieldSettingVisible || filterVisible || emptyState}
          loaderColor={'BLUE'}
        />
        <ModBox show={exportVaultReportsHandler.state === 'pending' || exportFileHandler.state === 'pending'}>
          <Loader color="BLUE" />
          <SpacerNew size={8} />
        </ModBox>
        {!filterVisible && !fieldSettingVisible && listData.length > 1 && (
          <Box {...scrollStyle} nativeID={'scroll-horizontal-Table'} f={1}>
            <NewTable
              data={listData.slice(1)}
              onRowClick={() => null}
              rowHeight={48}
              headerHeight={64}
              hide={!(!loadingState && !errorState && !getEmptyMessage())}
              customWidthFunc={getCustomColumnWidth}
              tableWidth={getWidth(viewPortWidth > 1200 ? viewPortWidth - 288 : viewPortWidth, listData?.[0])}
              headerRowKeys={listData?.[0]}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const styles = {
  container: {
    shadowOffset: { width: 1, height: 1 },
    shadowRadius: 3,
    shadowColor: Colors.LIGHT_GREY,
    borderWidth: 1,
    borderColor: Colors.BORDER_COLOR,
    zIndex: 10,
    backgroundColor: 'WHITE',
    paddingHorizontal: 16,
    paddingBottom: 16,
    marginHorizontal: 18,
    border: `1px solid ${Colors.SECONDARY_GREY}`,
    // justifyContent: 'space-between',
    borderRadius: 4,
    position: 'relative',
    flex: 1,
    overflow: 'auto',
  } as BoxProps,
  emptyContainer: {
    alignContent: 'center',
    flex: 1,
  } as BoxProps,
  tableContainer: (isLoading: boolean) =>
    ({
      alignContent: 'center',
      flexGrow: 1,
      marginTop: isLoading ? 24 : undefined,
    } as BoxProps),
}
export default Vault
